import { Outlet, createRootRoute } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense, useEffect } from "react";
import { Spinner } from "@heroui/react";
import NiceModal from "@ebay/nice-modal-react";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "@repo/context/auth/auth";
import { UserManager } from "@/service/user-manager";
import { queryClient } from "@/service/query-client";
import { applyLoginSession, resolveLoginSession } from "@/service/session-source";
import { VersionManager } from "@/utils/version-manager";
import { appConfig } from "@/config/app-config.ts";
import Log, { LOG_LEVEL } from "@repo/utils/log";
import { asSearchString } from "@repo/utils/search-params";
import { bridge } from "@/utils/rn-bridge";
import * as Aos from "aos";
import "aos/dist/aos.css";
import "@/locales/index.ts";
import "./-App.css";

export type RootSearch = {
  t?: string;
  ver?: string;
};

export const Route = createRootRoute({
  validateSearch: (search: Record<string, unknown>): RootSearch => ({
    t: asSearchString(search.t),
    ver: asSearchString(search.ver),
  }),
  beforeLoad: async ({ search }) => {
    if (search.ver) {
      VersionManager.saveVersion(search.ver);
    }
    // 登录态优先向 RN 索取，其次才用 URL ?t=；都没有则沿用 sessionStorage 已有会话
    const session = await resolveLoginSession(search.t);
    if (session) {
      applyLoginSession(session);
    }
  },
  component: RootComponent,
});

function RootComponent() {
  useEffect(() => {
    Aos.init({
      duration: 400,
      easing: "ease-in-out",
      once: true,
      mirror: false,
    });
    Log.setLogLevel(import.meta.env.MODE !== "production" ? LOG_LEVEL.DEBUG : LOG_LEVEL.ERROR);
    document.title = appConfig.appName;
    Log.debug("RNBridge mounted:", window.RNBridge);
    return () => {
      bridge.destroy();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <NiceModal.Provider>
        <AuthProvider userManager={UserManager}>
          <Suspense
            fallback={
              <div className="flex h-screen items-center justify-center">
                <div className="text-center">
                  <Spinner size="sm" />
                  <p className="text-gray-600">加载中...</p>
                </div>
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </AuthProvider>
        <Toaster />
        {import.meta.env.MODE !== "production" && <TanStackRouterDevtools position="bottom-right" />}
        {import.meta.env.MODE !== "production" && <ReactQueryDevtools buttonPosition="bottom-left" />}
      </NiceModal.Provider>
    </QueryClientProvider>
  );
}
